import { FLAGS } from 'affiny-core/helpers/featureToggle';
import { EReflistChapterName } from 'affiny-core/modules/api/repositories/reflists/reflists.types';
import { EBrand, EHostLang } from 'affiny-core/types/env.types';

const getBaseApiHostname = (env?: string) => {
  switch (env) {
    case 'prod':
      return `https://${process.env.REACT_APP_SUBDOMAIN}.${process.env.REACT_APP_BRAND}.${process.env.REACT_APP_TOP_LEVEL_DOMAIN}`;
    case 'kube':
      return `//${process.env.REACT_APP_NAMESPACE}.${process.env.REACT_APP_SUBDOMAIN}.${process.env.REACT_APP_BRAND}.${process.env.REACT_APP_TOP_LEVEL_DOMAIN}.kube.recette.ilius.io`;
    case 'local':
      return process.env.REACT_APP_LOCAL_API_URL;
    default:
      return getBaseApiHostname('prod');
  }
};

export const sharedConfigLocale = {
  meeticApi: {
    baseApiHostname: getBaseApiHostname(process.env.REACT_APP_ENV),
    baseApiPath: process.env.REACT_APP_ENV === 'local' ? '' : '/apimm',
  },
  fastForward: {
    disabled: process.env.REACT_APP_FF_DISABLED === 'true',
    chapterLabel: process.env.REACT_APP_FF_CHAPTER_NAME as EReflistChapterName,
    questionIndex: parseInt(process.env.REACT_APP_FF_QUESTION_INDEX as string),
    pageAction: process.env.REACT_APP_FF_PAGE_ACTION,
  },
};

export const sharedConfigKubePreprod = {
  publicBasePath: process.env.REACT_APP_DEVICE === 'mobile' ? '/m' : '/w',
  featureToggling: {
    [FLAGS.PHOTO_REVEAL_SEE_MORE_BUTTON]: false,
    [FLAGS.LOGIN_FROM_QUERY_PARAMS_DEBUG]: true,
    [FLAGS.LOGIN_DEBUG]: false,
  },
  googleTagManagerContainerIdForGoogleAnalytics: 'GTM-TWG8QKQ',
  googleTagManagerContainerIdForOneTrust: 'GTM-PGVTX2R',
};

export const sharedConfigProd = {
  ...sharedConfigKubePreprod,
  featureToggling: {
    [FLAGS.PHOTO_REVEAL_SEE_MORE_BUTTON]: false,
    [FLAGS.LOGIN_FROM_QUERY_PARAMS_DEBUG]: false,
    [FLAGS.LOGIN_DEBUG]: false,
  },
};

export const getDefaultNationalityIds = (
  brand: EBrand,
  hostLang: EHostLang
) => {
  switch (hostLang) {
    case EHostLang.FR:
      return [62, 20, 171]; // French, Belgium, Swiss
    case EHostLang.UK:
      return [152, 7, 62]; // British, English, French
    case EHostLang.NL:
      return [140, 5, 20]; // Dutch, German, Belgium
    case EHostLang.NO:
      return [130, 170, 61]; // Norwegian, Swedish, Finnish
    case EHostLang.SE:
      return [170, 130, 61]; // Swedish, Norwegian, Finnish
    case EHostLang.DK:
      return [48, 140, 5]; // Danish, Dutch, German
    case EHostLang.FI:
      return [61, 170, 130]; // Finnish, Swedish, Norwegian
    case EHostLang.IE:
      return [85, 152, 140]; // Irish, British, Dutch
    case EHostLang.IT:
      return [88, 62, 171]; // Italian, French, Suisse
    case EHostLang.ES:
      return [56, 62, 144]; // Spanish, French, Portugese
    case EHostLang.PT:
      return [144, 56, 62]; // Portugese, Spanish, French
    case EHostLang.BE:
      return [20, 62, 5]; // Belgium, French, German

    case EHostLang.CH:
      switch (brand) {
        case EBrand.PA:
          return [171, 5, 62]; // Swiss, German, French
        case EBrand.MAF:
          return [171, 62, 5]; // Swiss, French, German
        default:
          return [171, 5, 62]; // Swiss, German, French
      }
    case EHostLang.DE:
      return [5, 62, 20]; // German, French, Belgium
    default:
      return [62, 20, 171]; // French, Belgium, Swiss
  }
};

export const overrideConfig = (brand: EBrand, hostLang: EHostLang) => {
  const isMobile = process.env.REACT_APP_DEVICE === 'mobile';

  if (isMobile) {
    switch (hostLang) {
      case EHostLang.FR:
        return { googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB' };
      case EHostLang.UK:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.NL:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB',
        };
      case EHostLang.NO:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB',
        };
      case EHostLang.SE:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.DK:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.FI:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.IE:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.IT:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB',
        };
      case EHostLang.ES:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.PT:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };
      case EHostLang.BE:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
        };

      case EHostLang.CH:
        switch (brand) {
          case EBrand.PA:
            return {
              googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB',
            };
          case EBrand.MAF:
            return {
              googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
            };
          default:
            return {
              googleTagManagerContainerIdForGoogleAnalytics: 'GTM-W6XK35Z',
            };
        }
      case EHostLang.DE:
        return {
          googleTagManagerContainerIdForGoogleAnalytics: 'GTM-N5Q8LQB',
        };
    }
  } else {
    return {};
  }
};
